import { useEffect, useState, useCallback } from 'react';
import { offlineDB, pendingCount, getMeta, setMeta, enqueueAction } from './db';
import axios from 'axios';

const API = ((typeof window !== "undefined" && window.__API_URL__) || process.env.REACT_APP_BACKEND_URL) + '/api';

/**
 * Hook réseau : détecte online/offline et expose un compteur d'actions pendantes.
 */
export function useNetworkStatus() {
  const [online, setOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [pending, setPending] = useState(0);

  const refreshPending = useCallback(async () => {
    try {
      const c = await pendingCount();
      setPending(c);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    refreshPending();
    const i = setInterval(refreshPending, 5000);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(i);
    };
  }, [refreshPending]);

  return { online, pending, refreshPending };
}

/**
 * Sync engine : drain la queue lorsque le réseau revient.
 * Stratégie : Last-Write-Wins basé sur _updated_at_local.
 * Conflits HTTP 409/412 → conflict_log.
 */
export async function processSyncQueue({ onProgress } = {}) {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return { processed: 0, errors: 0 };
  const items = await offlineDB.sync_queue.orderBy('queue_id').toArray();
  if (items.length === 0) return { processed: 0, errors: 0 };
  let processed = 0;
  let errors = 0;
  let conflicts = 0;
  for (const item of items) {
    try {
      const url = endpointFor(item);
      if (!url) {
        await offlineDB.sync_queue.delete(item.queue_id);
        continue;
      }
      const config = { withCredentials: true };
      let res;
      if (item.action === 'create') {
        res = await axios.post(url, item.payload, config);
      } else if (item.action === 'update') {
        res = await axios.put(url, item.payload, config);
      } else if (item.action === 'delete') {
        res = await axios.delete(url, config);
      } else if (item.action === 'upload_photo') {
        const photo = await offlineDB.photos.get(item.entity_id);
        if (photo?.blob) {
          const fd = new FormData();
          const file = new File([photo.blob], photo.filename || `photo_${item.entity_id}.jpg`, { type: photo.mime_type || 'image/jpeg' });
          fd.set('file', file);
          const params = new URLSearchParams();
          if (photo.project_id) params.set('project_id', photo.project_id);
          if (photo.folder) params.set('folder', photo.folder);
          if (photo.subfolder) params.set('subfolder', photo.subfolder);
          res = await axios.post(`${API}/upload?${params.toString()}`, fd, {
            withCredentials: true,
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          // Mark photo as uploaded
          await offlineDB.photos.update(item.entity_id, { _local_status: 'synced', server_file_id: res.data.id });
        }
      }
      // Replace local cache with server response (LWW)
      if (res?.data && item.entity && item.entity !== 'photo') {
        const tbl = offlineDB[item.entity];
        if (tbl && res.data.id) {
          await tbl.put({ ...res.data, _local_status: 'synced', _updated_at_local: new Date().toISOString() });
        }
      }
      await offlineDB.sync_queue.delete(item.queue_id);
      processed++;
      onProgress?.({ processed, total: items.length, item });
    } catch (err) {
      errors++;
      const status = err?.response?.status;
      if (status === 409 || status === 412) {
        conflicts++;
        await offlineDB.conflict_log.add({
          entity: item.entity,
          entity_id: item.entity_id,
          action: item.action,
          payload: item.payload,
          server_error: err?.response?.data?.detail || 'Conflict',
          created_at: new Date().toISOString(),
        });
        await offlineDB.sync_queue.delete(item.queue_id);
      } else if (status === 401 || status === 403) {
        // Auth issue → arrêt et on laisse l'item en queue
        break;
      } else {
        // Erreur réseau ou serveur : on incrémente les tentatives, on garde
        const newAttempts = (item.attempts || 0) + 1;
        if (newAttempts >= 5) {
          // Trop d'échecs : on bascule en conflit pour que la file ne reste pas bloquée
          await offlineDB.conflict_log.add({
            entity: item.entity,
            entity_id: item.entity_id,
            action: item.action,
            payload: item.payload,
            server_error: `Échec après 5 tentatives : ${err?.message || status || 'unknown'}`,
            created_at: new Date().toISOString(),
          });
          await offlineDB.sync_queue.delete(item.queue_id);
          conflicts++;
        } else {
          await offlineDB.sync_queue.update(item.queue_id, {
            attempts: newAttempts,
            last_error: err?.message || 'unknown',
          });
        }
      }
    }
  }
  await setMeta('last_sync_at', new Date().toISOString());
  return { processed, errors, conflicts };
}

function endpointFor(item) {
  const { entity, action, entity_id } = item;
  switch (entity) {
    case 'visits':
      if (action === 'create') return `${API}/visits`;
      if (action === 'update') return `${API}/visits/${entity_id}`;
      if (action === 'delete') return `${API}/visits/${entity_id}`;
      return null;
    case 'studies':
      if (action === 'create') return `${API}/studies`;
      if (action === 'update') return `${API}/studies/${entity_id}`;
      if (action === 'delete') return `${API}/studies/${entity_id}`;
      return null;
    case 'bordereau':
      if (action === 'update') return `${API}/bordereau`;
      return null;
    default:
      return null;
  }
}

/**
 * Pull différentiel (ou complet en V1) : télécharge les visites & BPU récents pour usage offline.
 */
export async function prefetchForOffline() {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return;
  try {
    const [visitsRes, bordereauRes, propertiesRes, templateRes] = await Promise.all([
      axios.get(`${API}/visits`, { withCredentials: true }),
      axios.get(`${API}/bordereau`, { withCredentials: true }).catch(() => null),
      axios.get(`${API}/properties`, { withCredentials: true }).catch(() => null),
      axios.get(`${API}/visits/template`, { withCredentials: true }).catch(() => null),
    ]);
    await offlineDB.transaction('rw', offlineDB.visits, offlineDB.bordereau, async () => {
      const pendings = await offlineDB.visits.where('_local_status').equals('pending').primaryKeys();
      const pSet = new Set(pendings);
      for (const v of (visitsRes.data || [])) {
        if (!pSet.has(v.id)) {
          await offlineDB.visits.put({ ...v, _local_status: 'synced' });
        }
      }
      if (bordereauRes?.data?.items) {
        for (const item of bordereauRes.data.items) {
          await offlineDB.bordereau.put({ ...item, _local_status: 'synced' });
        }
      }
    });
    if (bordereauRes?.data) await setMeta('bordereau_doc', bordereauRes.data);
    if (propertiesRes?.data) await setMeta('properties_cache', propertiesRes.data);
    if (templateRes?.data) await setMeta('visit_template_cache', templateRes.data);
    await setMeta('last_prefetch_at', new Date().toISOString());
  } catch (e) {
    console.warn('Prefetch failed:', e);
  }
}

export { enqueueAction };
